import { Routes } from '@angular/router';
import { OnboardingShellComponent } from './shared/components/onboarding-shell/onboarding-shell.component';
import { LoginComponent } from './pages/onboarding/login/login.component';
import { ManagementWrapperComponent } from './pages/management/management-wrapper.component';
import { DashboardComponent } from './pages/management/dashboard/dashboard.component';
import { EmployeesListComponent } from './pages/management/employees/active-employees/list/employees-list.component';
import { AddEmployeeLandingComponent } from './pages/management/employees/add-employee/add-employee-landing/add-employee-landing.component';
import { AddEmployeeDetailsComponent } from './pages/management/employees/add-employee/add-employee-details/add-employee-details.component';
import { ViewEmployeeDetailsComponent } from './pages/management/employees/active-employees/detail-view/view-employee-details.component';
import { EditEmployeeDetailsComponent } from './pages/management/employees/active-employees/edit-view/edit-employee-details.component';
import { AuthenticatedGuard } from '@bo/ng-auth';
import { canActivateWithReturnUrl } from './guards/authenticated-with-return-url.guard';
import { canActivateUnAuthenticated } from './guards/unauthenticated.guard';
import { FundAccountComponent } from './pages/management/fund-account/fund/fund-account.component';
import { YourPlanComponent } from './pages/management/your-plan/your-plan.component';
import { ManagePlanComponent } from './pages/management/your-plan/manage-plan/manage-plan.component';
import { UpgradePlanComponent } from './pages/management/your-plan/upgrade-plan/upgrade-plan.component';
import { RemoveApproverComponent } from './pages/management/employees/active-employees/remove-approver/remove-approver.component';
import { ForgotPasswordComponent } from './pages/onboarding/forgot-password/forgot-password.component';
import { CreatePasswordForgotPasswordComponent } from './pages/onboarding/forgot-password/create-password/create-password-forgot-password.component';
import { CreateSubscriptionCardComponent } from './pages/management/cards/subscription-cards/create/create-subscription-card.component';
import { EmployeeCardsComponent } from './pages/management/cards/employee-cards/employee-cards.component';
import { SubscriptionCardsComponent } from './pages/management/cards/subscription-cards/subscription-cards.component';
import { InvitedEmployeesListComponent } from './pages/management/employees/invited-employees/list/invited-employees-list.component';
import { GetStartedComponent } from './pages/onboarding/sign-up/get-started/get-started.component';
import { BusinessDetailsComponent } from './pages/onboarding/sign-up/business-details/business-details.component';
import { VerifyEmailComponent } from './pages/onboarding/sign-up/verify-email/verify-email.component';
import { CreatePasswordSignUpComponent } from './pages/onboarding/sign-up/create-password/create-password-sign-up.component';
import { BusinessCertificateComponent } from './pages/onboarding/sign-up/business-certificate/business-certificate.component';
import { VerifyBusinessComponent } from './pages/onboarding/sign-up/verify-business/verify-business.component';
import { SubscriptionSelectionComponent } from './pages/onboarding/sign-up/subscription-selection/subscription-selection.component';
import { DirectorsApprovalComponent } from './pages/onboarding/sign-up/director-approval/director-approval.component';
import { canActivatePhoneAlreadyVerified, canActivatePhoneVerified } from './guards/sign-up/verify-phone-guard.service';
import {
  canActivateHasSubscription,
  canActivateSubscription,
} from './guards/sign-up/select-subscription-guard.service';
import {
  canActivateBusinessCert,
  canActivateHasBusinessCert,
  canActivateHasBusinessCertError,
} from './guards/sign-up/business-cert.guard';
import { DirectorApprovalRedirectComponent } from './pages/onboarding/sign-up/director-approval/director-approval-redirect.component';
import { HelpDeskComponent } from './pages/management/help-center/help-desk/help-desk.component';
import { CreatePasswordEmployeeComponent } from './pages/onboarding/employee-sign-up/create-password/create-password-employee.component';
import { EmployeeSignUpSuccessComponent } from './pages/onboarding/employee-sign-up/sign-up-success/employee-sign-up-success.component';
import { PageNotFoundComponent } from './shared/components/error-pages/page-not-found/page-not-found.component';
import { PasswordChangeComponent } from './pages/management/settings/profile/password-change/password-change.component';
import { FaqsComponent } from './pages/management/help-center/faqs/faqs.component';
import { UpdateContactDetailsComponent } from './pages/update/update-contact-details/update-contact-details.component';
import { EmailSuccessComponent } from './pages/update/email-success/email-success.component';
import { UpdateErrorComponent } from './pages/update/update-error/update-error.component';
import { PhoneSuccessComponent } from './pages/update/phone-success/phone-success.component';
import { SubscriptionCardDetailComponent } from './pages/management/cards/subscription-cards/detail-view/subscription-card-detail.component';
import { canActivateAccountLock } from './guards/account-lock.service';
import { EmployeeCardDetailComponent } from './pages/management/cards/employee-cards/detail-view/employee-card-detail.component';
import { BlockEmployeeCardComponent } from './pages/management/cards/employee-cards/block-employee-card/block-employee-card.component';
import { BlockSubscriptionCardComponent } from './pages/management/cards/subscription-cards/block-subscription-card/block-subscription-card.component';
import { BlockSubscriptionCardConfirmationComponent } from './pages/management/cards/subscription-cards/block-subscription-card-confirmation/block-subscription-card-confirmation.component';
import { BlockEmployeeCardConfirmationComponent } from './pages/management/cards/employee-cards/block-employee-card-confirmation/block-employee-card-confirmation.component';
import { ReplaceSubscriptionCardComponent } from './pages/management/cards/subscription-cards/replace-subscription-card/replace-subscription-card.component';
import { ReplaceEmployeeCardComponent } from './pages/management/cards/employee-cards/replace-employee-card/replace-employee-card.component';
import { RemoveSubscriptionCardComponent } from './pages/management/cards/subscription-cards/remove-subscription-card/remove-subscription-card.component';
import { SettingsComponent } from './pages/management/settings/settings.component';
import { VerifyPhoneComponent } from './pages/onboarding/verify-phone/verify-phone.component';
import { EditBusinessDetailsComponent } from './pages/management/settings/business-details/edit-business-details/edit-business-details.component';
import { EditBankingDetailsComponent } from './pages/management/settings/banking-details/edit/edit-banking-details.component';
import { IntegrationsComponent } from './pages/management/accounting/integrations/integrations.component';
import { AccountingComponent } from './pages/management/accounting/accounting.component';
import { TransactionsComponent } from './pages/management/accounting/transactions/transactions.component';
import { TransactionsEditComponent } from './pages/management/accounting/transactions/transactions-edit/transactions-edit.component';
import { CategoriesComponent } from './pages/management/accounting/categories/categories.component';
import { AccountConnectionComponent } from './pages/management/accounting/integrations/account-connection-redirect/account-connection.component';
import { TenantAccountComponent } from './pages/management/accounting/integrations/tenant-account/tenant-account.component';
import { TransactionsExportComponent } from './pages/management/accounting/transactions/transactions-export/transactions-export.component';
import { BankingDetailsAuthorisationComponent } from './pages/management/settings/banking-details/directors-authorisation/banking-details-authorisation.component';
import { canActivateTermsAcceptGuard } from './guards/terms-accept.guard';
import { TermsUpdatedAcceptComponent } from './pages/onboarding/terms-updated-accept/terms-updated-accept.component';
import { VerifyIdentityComponent } from './pages/onboarding/verify-identity/verify-identity.component';
import { EditInvitedEmployeeComponent } from './pages/management/employees/invited-employees/edit-view/edit-invited-employee.component';
import { ImmediateTopupsComponent } from './pages/management/card-topups/immediate-topups/immediate-topups.component';
import { RecurringTopupsComponent } from './pages/management/card-topups/recurring-topups/recurring-topups.component';
import { ScheduledTopupsComponent } from './pages/management/card-topups/scheduled-topups/scheduled-topups.component';
import { CardTopupCanvasComponent } from './pages/management/card-topups/edit/card-topup-canvas/card-topup-canvas.component';
import { ImmediateTopUpCreateComponent } from './pages/management/card-topups/immediate-topups/immediate-topup-form/immediate-top-up-create.component';
import { DocumentsPageComponent } from './pages/onboarding/documents-page/documents-page.component';
import { HowToVideosComponent } from './pages/management/help-center/how-to-videos/how-to-videos.component';
import { canActivateOffcanvas } from './guards/offcanvas-access.guard';
import { ContraAccountsComponent } from './pages/management/accounting/integrations/contra-accounts/contra-accounts.component';
import { SourceAccountComponent } from './pages/management/accounting/integrations/source-account/source-account.component';

export const routes: Routes = [
  {
    title: 'Sign Up',
    path: 'sign-up',
    component: OnboardingShellComponent,
    children: [
      {
        path: '',
        redirectTo: 'get-started',
        pathMatch: 'full',
      },
      {
        title: 'Get Started',
        path: 'get-started',
        component: GetStartedComponent,
        canActivate: [canActivateUnAuthenticated],
      },
      {
        title: 'Business Details',
        path: 'business-details',
        component: BusinessDetailsComponent,
        canActivate: [canActivateUnAuthenticated],
      },
      {
        title: 'Verify Email',
        path: 'verify-email',
        component: VerifyEmailComponent,
        canActivate: [canActivateUnAuthenticated],
      },
      {
        title: 'Create Password',
        path: 'create-password',
        component: CreatePasswordSignUpComponent,
        canActivate: [canActivateUnAuthenticated],
      },
      {
        title: 'Verify Phone',
        path: 'verify-phone',
        component: VerifyPhoneComponent,
        canActivate: [AuthenticatedGuard, canActivatePhoneAlreadyVerified],
      },
      {
        title: 'Subscription Selection',
        path: 'subscription-selection',
        component: SubscriptionSelectionComponent,
        canActivate: [AuthenticatedGuard, canActivateHasSubscription],
      },
      {
        title: 'Business Certificate',
        path: 'business-certificate',
        component: BusinessCertificateComponent,
        canActivate: [AuthenticatedGuard, canActivateHasBusinessCert],
      },
      {
        title: 'Business Certificate',
        path: 'business-certificate-retry',
        component: BusinessCertificateComponent,
        canActivate: [AuthenticatedGuard, canActivateHasBusinessCertError],
      },
      {
        title: 'Verify Business',
        path: 'verify-business',
        component: VerifyBusinessComponent,
      },
      {
        path: 'director-approval/:whatsappApproval',
        component: DirectorApprovalRedirectComponent,
      },
      {
        title: 'Director Approval',
        path: 'director-approval',
        component: DirectorsApprovalComponent,
      },

      //employee sign up
      {
        title: 'Create Password',
        path: 'employee/create-password',
        component: CreatePasswordEmployeeComponent,
        canActivate: [canActivateUnAuthenticated],
      },
      {
        title: 'Verify Phone',
        path: 'employee/verify-phone',
        component: VerifyPhoneComponent,
        canActivate: [AuthenticatedGuard, canActivatePhoneAlreadyVerified],
      },
      {
        title: 'Successful',
        path: 'employee/success',
        component: EmployeeSignUpSuccessComponent,
      },
    ],
  },
  {
    title: 'Terms and Conditions',
    path: 'terms-and-conditions',
    component: DocumentsPageComponent,
  },
  {
    title: 'Prepaid Card Terms and Conditions',
    path: 'prepaid-card-terms-and-conditions',
    component: DocumentsPageComponent,
  },
  {
    title: 'Privacy Policy',
    path: 'privacy-policy',
    component: DocumentsPageComponent,
  },
  {
    title: 'Document Preview',
    path: 'document-preview',
    component: DocumentsPageComponent,
  },
  {
    title: 'Verify Your Identity',
    path: 'verify-identity',
    component: OnboardingShellComponent,
    children: [
      {
        title: 'Verify Your Identity',
        path: '',
        component: VerifyIdentityComponent,
        canActivate: [AuthenticatedGuard],
      },
    ],
  },
  {
    path: 'update',
    component: OnboardingShellComponent,
    children: [
      {
        title: 'Verify Email',
        path: 'email',
        children: [
          {
            path: '',
            component: UpdateContactDetailsComponent,
          },
          {
            path: 'success',
            title: 'Success',
            component: EmailSuccessComponent,
          },
          {
            path: 'error',
            title: 'Error',
            component: UpdateErrorComponent,
          },
        ],
      },
      {
        title: 'Verify Phone',
        path: 'phone',
        children: [
          {
            path: '',
            component: UpdateContactDetailsComponent,
          },
          {
            path: 'success',
            title: 'Success',
            component: PhoneSuccessComponent,
          },
          {
            path: 'error',
            title: 'Error',
            component: UpdateErrorComponent,
          },
        ],
      },
      {
        title: 'Accept updated Terms of Use',
        path: 'terms-of-use',
        component: TermsUpdatedAcceptComponent,
        canActivate: [AuthenticatedGuard],
      },
    ],
  },
  {
    title: 'Login',
    path: 'login',
    component: OnboardingShellComponent,
    canActivate: [canActivateUnAuthenticated],
    children: [
      {
        path: '',
        component: LoginComponent,
      },
    ],
  },
  {
    title: 'Help Desk',
    path: 'help-desk',
    component: HelpDeskComponent,
  },
  {
    title: 'Reset Password',
    path: 'forgot-password',
    component: OnboardingShellComponent,
    children: [
      {
        path: '',
        component: ForgotPasswordComponent,
      },
      {
        title: 'Create Password',
        path: 'create-password',
        component: CreatePasswordForgotPasswordComponent,
      },
    ],
  },
  {
    path: 'directors-authorisation',
    children: [
      {
        title: 'Authorise banking details',
        path: 'banking-details/:token',
        component: BankingDetailsAuthorisationComponent,
      },
    ],
  },
  {
    path: '',
    component: ManagementWrapperComponent,
    canActivate: [
      canActivateWithReturnUrl,
      canActivateAccountLock,
      canActivatePhoneVerified,
      canActivateSubscription,
      canActivateTermsAcceptGuard,
      canActivateBusinessCert,
    ],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        title: 'Dashboard',
        path: 'dashboard',
        component: DashboardComponent,
      },
      {
        title: 'Employees',
        path: 'employees',
        component: EmployeesListComponent,
        children: [
          {
            path: 'add',
            canActivate: [canActivateOffcanvas],
            children: [
              {
                // Temporarily changed,
                path: '',
                redirectTo: 'employee-details',
                pathMatch: 'full',
                // Return to the original state after bulk upload is enabled again
                // path: '',
                //component: AddEmployeeLandingComponent,
              },
              {
                path: 'employee-details',
                component: AddEmployeeDetailsComponent,
              },
            ],
          },
          // Temporarily disabled
          // {
          //   path: 'bulk-upload-template',
          //   component: BulkUploadTemplateComponent,
          //   canActivate: [canActivateOffcanvas],
          // },
          // {
          //   path: 'bulk-upload',
          //   component: BulkUploadComponent,
          //   canActivate: [canActivateOffcanvas],
          // },
          // {
          //   path: 'bulk-upload/:id',
          //   component: BulkUploadDetailComponent,
          //   canActivate: [canActivateOffcanvas],
          // },
          {
            path: 'view/:id',
            component: ViewEmployeeDetailsComponent,
            canActivate: [canActivateOffcanvas],
          },
          {
            path: 'edit/:id',
            component: EditEmployeeDetailsComponent,
            canActivate: [canActivateOffcanvas],
          },
          {
            path: 'remove/:id',
            component: RemoveApproverComponent,
            canActivate: [canActivateOffcanvas],
          },
        ],
      },
      {
        title: 'Invited Employees',
        path: 'employee-invitations',
        component: InvitedEmployeesListComponent,
        children: [
          {
            path: 'add',
            canActivate: [canActivateOffcanvas],
            children: [
              {
                path: '',
                component: AddEmployeeLandingComponent,
              },
              {
                path: 'employee-details',
                component: AddEmployeeDetailsComponent,
              },
            ],
          },
          // Temporarily disabled
          // {
          //   path: 'bulk-upload',
          //   component: BulkUploadComponent,
          //   canActivate: [canActivateOffcanvas],
          // },
          // {
          //   path: 'bulk-upload/:id',
          //   component: BulkUploadDetailComponent,
          //   canActivate: [canActivateOffcanvas],
          // },
          {
            path: 'edit/:id',
            component: EditInvitedEmployeeComponent,
            canActivate: [canActivateOffcanvas],
          },
        ],
      },
      {
        path: 'cards',
        children: [
          {
            path: '',
            redirectTo: 'employee-cards',
            pathMatch: 'full',
          },
          {
            path: 'employee-cards',
            component: EmployeeCardsComponent,
            title: 'Employee Cards',
            children: [
              {
                path: 'view/:id',
                component: EmployeeCardDetailComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'block-card/:id',
                component: BlockEmployeeCardComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'block-card-complete/:id',
                component: BlockEmployeeCardConfirmationComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'replace-card/:id',
                component: ReplaceEmployeeCardComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
          {
            path: 'subscription-cards',
            component: SubscriptionCardsComponent,
            title: 'Subscription Cards',
            children: [
              {
                path: 'create',
                component: CreateSubscriptionCardComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'view/:id',
                component: SubscriptionCardDetailComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'block-card/:id',
                component: BlockSubscriptionCardComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'block-card-complete/:id',
                component: BlockSubscriptionCardConfirmationComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'replace-card/:id',
                component: ReplaceSubscriptionCardComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'remove-card/:id',
                component: RemoveSubscriptionCardComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
        ],
      },
      {
        path: 'card-topups',
        children: [
          {
            title: 'Immediate top-ups',
            path: 'immediate',
            component: ImmediateTopupsComponent,
            children: [
              {
                path: 'add',
                component: ImmediateTopUpCreateComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
          {
            title: 'Recurring top-ups',
            path: 'recurring',
            component: RecurringTopupsComponent,
            children: [
              {
                path: 'add',
                component: CardTopupCanvasComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'edit/:id',
                component: CardTopupCanvasComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
          {
            title: 'Scheduled top-ups',
            path: 'scheduled',
            component: ScheduledTopupsComponent,
            children: [
              {
                path: 'add',
                component: CardTopupCanvasComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'edit/:id',
                component: CardTopupCanvasComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
        ],
      },
      {
        path: 'fund-account',
        title: 'Fund Account',
        component: FundAccountComponent,
      },
      {
        title: 'Accounting',
        path: 'accounting',
        component: AccountingComponent,
        children: [
          {
            path: '',
            redirectTo: 'integrations',
            pathMatch: 'full',
          },
          {
            path: 'integrations',
            component: IntegrationsComponent,
          },
          {
            path: 'connect',
            children: [
              {
                path: 'redirect',
                component: AccountConnectionComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'select-organisation',
                component: TenantAccountComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'select-source-account',
                component: SourceAccountComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'contra-accounts',
                component: ContraAccountsComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
          {
            title: 'Transactions',
            path: 'transactions',
            component: TransactionsComponent,
            children: [
              {
                path: 'edit/:id',
                component: TransactionsEditComponent,
                canActivate: [canActivateOffcanvas],
              },
              {
                path: 'export',
                component: TransactionsExportComponent,
                canActivate: [canActivateOffcanvas],
              },
            ],
          },
          {
            title: 'Categories',
            path: 'categories',
            component: CategoriesComponent,
          },
        ],
      },
      {
        title: 'Your Plan',
        path: 'your-plan',
        component: YourPlanComponent,
        children: [
          {
            path: 'manage',
            component: ManagePlanComponent,
          },
          {
            path: 'upgrade-plan',
            component: UpgradePlanComponent,
          },
          {
            path: 'edit-business-details/:id',
            component: EditBusinessDetailsComponent,
          },
        ],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        children: [
          {
            path: 'change-password',
            component: PasswordChangeComponent,
          },
          {
            path: 'edit-business-details/:id',
            component: EditBusinessDetailsComponent,
          },
          {
            path: 'edit-banking-details',
            component: EditBankingDetailsComponent,
          },
        ],
      },
      {
        path: 'help-center',
        children: [
          {
            path: '',
            redirectTo: 'faq',
            pathMatch: 'full',
          },
          {
            title: 'Frequently asked Questions',
            path: 'faq',
            component: FaqsComponent,
          },
          {
            title: 'How to videos',
            path: 'how-to-videos',
            component: HowToVideosComponent,
          },
        ],
      },
    ],
  },
  {
    title: 'Page Not Found',
    path: '**',
    component: PageNotFoundComponent,
  },
];
